// src/sections/gamification/plays/confetti.js
// Tiny hand-rolled canvas particle engine shared by the play reveals — burst
// confetti, directional sprays and sparkles. No deps; one rAF loop per run
// that self-terminates when every particle dies.

// Astra plays palette only: gold, violet, cyan, white — no strays.
const COLORS = ['#f5c644', '#8b5cf6', '#a78bfa', '#22d3ee', '#ffe9a3', '#f5f3ff'];

export function particleBurst(
  canvas,
  {
    x,
    y,
    count = 120,
    power = 9,
    gravity = 0.22,
    colors = COLORS,
    shapes = ['rect', 'circle'],
    spread = Math.PI * 2,
    angle = -Math.PI / 2,
    drag = 0.985,
    fade = 0.012
  } = {}
) {
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  if (canvas.width !== canvas.clientWidth * dpr) {
    canvas.width = canvas.clientWidth * dpr;
    canvas.height = canvas.clientHeight * dpr;
    ctx.scale(dpr, dpr);
  }
  const parts = [];
  for (let i = 0; i < count; i += 1) {
    const a = angle + (Math.random() - 0.5) * spread;
    const v = power * (0.35 + Math.random() * 0.65);
    parts.push({
      x,
      y,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v,
      size: 3 + Math.random() * 5,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.3,
      color: colors[(Math.random() * colors.length) | 0],
      shape: shapes[(Math.random() * shapes.length) | 0],
      life: 1
    });
  }
  let raf;
  const tick = () => {
    ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    let alive = 0;
    for (const p of parts) {
      if (p.life <= 0) continue;
      p.vx *= drag;
      p.vy = p.vy * drag + gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      p.life -= fade;
      if (p.life <= 0 || p.y > canvas.clientHeight + 20) continue;
      alive += 1;
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      }
      ctx.restore();
    }
    if (alive > 0) raf = requestAnimationFrame(tick);
    else ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}

// Continuous upward spray (champagne foam) — returns a stop() that lets the
// remaining particles die naturally.
export function spray(canvas, { x, y, rate = 6, power = 11, colors = ['#fffde7', '#fff9c4', '#ffffff', '#f2ce56'], spread = 0.55 } = {}) {
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  if (canvas.width !== canvas.clientWidth * dpr) {
    canvas.width = canvas.clientWidth * dpr;
    canvas.height = canvas.clientHeight * dpr;
    ctx.scale(dpr, dpr);
  }
  const parts = [];
  let emitting = true;
  let raf;
  const tick = () => {
    if (emitting) {
      for (let i = 0; i < rate; i += 1) {
        const a = -Math.PI / 2 + (Math.random() - 0.5) * spread;
        const v = power * (0.5 + Math.random() * 0.6);
        parts.push({
          x: x + (Math.random() - 0.5) * 8,
          y,
          vx: Math.cos(a) * v,
          vy: Math.sin(a) * v,
          size: 2 + Math.random() * 4,
          color: colors[(Math.random() * colors.length) | 0],
          life: 1
        });
      }
    }
    ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    let alive = 0;
    for (const p of parts) {
      if (p.life <= 0) continue;
      p.vy += 0.3;
      p.x += p.vx;
      p.y += p.vy;
      p.life -= 0.014;
      if (p.life <= 0) continue;
      alive += 1;
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    if (alive > 0 || emitting) raf = requestAnimationFrame(tick);
    else ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
  };
  raf = requestAnimationFrame(tick);
  return {
    stop: () => {
      emitting = false;
    },
    kill: () => cancelAnimationFrame(raf)
  };
}
